import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { 
  getProducts, 
  createProduct, 
  updateProduct, 
  deleteProduct,
  getCustomers,
  createCustomer,
  getTransactions,
  getBankAccounts,
  createBankAccount,
  deleteBankAccount
} from '../services/api';
import { ShoppingBag, LogOut, Users, Package, Receipt, CreditCard, Plus, Edit, Trash2, DollarSign, Settings } from 'lucide-react';
import SettingsModal from './SettingsModal';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Product form state
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    image: '',
    stock: ''
  });

  // Customer form state
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    username: '',
    password: '',
    email: ''
  });

  // Bank account form state
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [bankForm, setBankForm] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    routingNumber: ''
  });

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, [user]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [productsData, customersData, transactionsData, bankAccountsData] = await Promise.all([
        getProducts(),
        getCustomers(),
        getTransactions(),
        user?.id ? getBankAccounts(user.id) : Promise.resolve([])
      ]);
      
      setProducts(productsData);
      setCustomers(customersData);
      setTransactions(transactionsData);
      setBankAccounts(bankAccountsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load some data. Please refresh.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProductSubmit = async () => {
    if (!productForm.name || !productForm.category || !productForm.price) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const productData = {
        name: productForm.name,
        category: productForm.category,
        price: parseFloat(productForm.price),
        description: productForm.description,
        image: productForm.image,
        stock: parseInt(productForm.stock) || 0
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast({
          title: 'Product updated',
          description: 'Product has been updated successfully'
        });
      } else {
        await createProduct(productData);
        toast({
          title: 'Product added',
          description: 'New product has been added successfully'
        });
      }

      await loadAllData();
      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({
        name: '',
        category: '',
        price: '',
        description: '',
        image: '',
        stock: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save product',
        variant: 'destructive'
      });
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      description: product.description,
      image: product.image,
      stock: product.stock.toString()
    });
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await deleteProduct(productId);
      toast({
        title: 'Product deleted',
        description: 'Product has been removed'
      });
      await loadAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete product',
        variant: 'destructive'
      });
    }
  };

  const handleCustomerSubmit = async () => {
    if (!customerForm.username || !customerForm.password || !customerForm.email) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      await createCustomer(customerForm);
      toast({
        title: 'Customer created',
        description: `Customer account created for ${customerForm.username}. They can now login!`
      });

      await loadAllData();
      setShowCustomerDialog(false);
      setCustomerForm({
        username: '',
        password: '',
        email: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create customer',
        variant: 'destructive'
      });
    }
  };

  const handleBankAccountSubmit = async () => {
    if (!bankForm.accountName || !bankForm.accountNumber) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      await createBankAccount({
        ...bankForm,
        adminId: user.id
      });
      
      toast({
        title: 'Bank account added',
        description: 'Bank account has been added and saved permanently!'
      });

      await loadAllData();
      setShowBankDialog(false);
      setBankForm({
        accountName: '',
        accountNumber: '',
        bankName: '',
        routingNumber: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add bank account',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    try {
      await deleteBankAccount(accountId);
      toast({
        title: 'Bank account deleted',
        description: 'Bank account has been removed'
      });
      await loadAllData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete bank account',
        variant: 'destructive'
      });
    }
  };

  const totalRevenue = transactions.reduce((sum, t) => sum + t.total, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-8 w-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">DressPay Admin</h1>
                <p className="text-sm text-gray-600">Administrator Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products</p>
                  <p className="text-3xl font-bold">{products.length}</p>
                </div>
                <Package className="h-12 w-12 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Customers</p>
                  <p className="text-3xl font-bold">{customers.length}</p>
                </div>
                <Users className="h-12 w-12 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-3xl font-bold">{transactions.length}</p>
                </div>
                <Receipt className="h-12 w-12 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-3xl font-bold">${totalRevenue.toFixed(2)}</p>
                </div>
                <DollarSign className="h-12 w-12 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList>
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="customers">
              <Users className="h-4 w-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <Receipt className="h-4 w-4 mr-2" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="banking">
              <CreditCard className="h-4 w-4 mr-2" />
              Banking
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Products</CardTitle>
                    <CardDescription>Add, edit, or remove products from your store</CardDescription>
                  </div>
                  <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setEditingProduct(null);
                        setProductForm({
                          name: '',
                          category: '',
                          price: '',
                          description: '',
                          image: '',
                          stock: ''
                        });
                      }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Product
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                        <DialogDescription>
                          {editingProduct ? 'Update product information' : 'Add a new product to your store'}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="name">Product Name *</Label>
                          <Input
                            id="name"
                            value={productForm.name}
                            onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                            placeholder="Enter product name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="category">Category *</Label>
                          <Select
                            value={productForm.category}
                            onValueChange={(value) => setProductForm({ ...productForm, category: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Fruits & Drinks">Fruits & Drinks ($1-20)</SelectItem>
                              <SelectItem value="Toys & Baby Clothes">Toys & Baby Clothes ($20-60)</SelectItem>
                              <SelectItem value="Diapers">Diapers ($60-70)</SelectItem>
                              <SelectItem value="Men & Women Clothes">Men & Women Clothes ($70-130)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="price">Price ($) *</Label>
                            <Input
                              id="price"
                              type="number"
                              step="0.01"
                              value={productForm.price}
                              onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label htmlFor="stock">Stock</Label>
                            <Input
                              id="stock"
                              type="number"
                              value={productForm.stock}
                              onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                              placeholder="100"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            id="description"
                            value={productForm.description}
                            onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                            placeholder="Product description"
                          />
                        </div>
                        <div>
                          <Label htmlFor="image">Image URL</Label>
                          <Input
                            id="image"
                            value={productForm.image}
                            onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                          />
                        </div>
                        <Button onClick={handleProductSubmit} className="w-full">
                          {editingProduct ? 'Update Product' : 'Add Product'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No products yet. Add your first product!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div key={product.id} className="flex items-center gap-4 border rounded-lg p-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg">{product.name}</h3>
                              <Badge variant="secondary" className="mt-1">{product.category}</Badge>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-indigo-600">${product.price.toFixed(2)}</p>
                              <p className="text-sm text-gray-600">Stock: {product.stock}</p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{product.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Customers</CardTitle>
                    <CardDescription>Create customer accounts - they persist after logout!</CardDescription>
                  </div>
                  <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Customer
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Customer Account</DialogTitle>
                        <DialogDescription>Add a new customer who can login and shop</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="cust-username">Username *</Label>
                          <Input
                            id="cust-username"
                            value={customerForm.username}
                            onChange={(e) => setCustomerForm({ ...customerForm, username: e.target.value })}
                            placeholder="Enter username"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cust-password">Password *</Label>
                          <Input
                            id="cust-password"
                            type="password"
                            value={customerForm.password}
                            onChange={(e) => setCustomerForm({ ...customerForm, password: e.target.value })}
                            placeholder="Enter password"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cust-email">Email *</Label>
                          <Input
                            id="cust-email"
                            type="email"
                            value={customerForm.email}
                            onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                            placeholder="customer@example.com"
                          />
                        </div>
                        <Button onClick={handleCustomerSubmit} className="w-full">
                          Create Customer
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {customers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No customers yet. Create your first customer account!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {customers.map((customer) => (
                      <div key={customer.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{customer.username}</h3>
                            <p className="text-sm text-gray-600">{customer.email}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <Badge variant="outline">ID: {customer.id}</Badge>
                              <span className="text-sm text-gray-600">Password: {customer.password}</span>
                              <span className="text-sm text-gray-600">Created: {customer.createdAt}</span>
                            </div>
                          </div>
                          <Badge variant="default">{customer.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>All Transactions</CardTitle>
                <CardDescription>View all customer transactions and orders</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge>{transaction.billLabel}</Badge>
                              <Badge variant="outline">{transaction.status}</Badge>
                              <Badge variant="secondary">Code: {transaction.confirmationCode}</Badge>
                            </div>
                            <p className="font-semibold">Customer: {transaction.customerName}</p>
                            <p className="text-sm text-gray-600">
                              {new Date(transaction.date).toLocaleDateString()} at{' '}
                              {new Date(transaction.date).toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-indigo-600">
                              ${transaction.total.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-600">{transaction.paymentMethod}</p>
                          </div>
                        </div>
                        <div className="space-y-2 bg-gray-50 p-3 rounded">
                          {transaction.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>
                                {item.name} x {item.quantity}
                              </span>
                              <span className="font-medium">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banking Tab */}
          <TabsContent value="banking">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Bank Accounts & Payment Methods</CardTitle>
                    <CardDescription>Add your bank accounts or cards - they save permanently!</CardDescription>
                  </div>
                  <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Bank Account / Card
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Payment Method</DialogTitle>
                        <DialogDescription>Add a bank account or debit/credit card</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="account-name">Account/Card Name *</Label>
                          <Input
                            id="account-name"
                            value={bankForm.accountName}
                            onChange={(e) => setBankForm({ ...bankForm, accountName: e.target.value })}
                            placeholder="DressPay Business Account"
                          />
                        </div>
                        <div>
                          <Label htmlFor="account-number">Account/Card Number *</Label>
                          <Input
                            id="account-number"
                            value={bankForm.accountNumber}
                            onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                            placeholder="************1234"
                          />
                        </div>
                        <div>
                          <Label htmlFor="bank-name">Bank Name</Label>
                          <Input
                            id="bank-name"
                            value={bankForm.bankName}
                            onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                            placeholder="Chase Bank, Wells Fargo, etc."
                          />
                        </div>
                        <div>
                          <Label htmlFor="routing-number">Routing Number</Label>
                          <Input
                            id="routing-number"
                            value={bankForm.routingNumber}
                            onChange={(e) => setBankForm({ ...bankForm, routingNumber: e.target.value })}
                            placeholder="*********5678"
                          />
                        </div>
                        <Button onClick={handleBankAccountSubmit} className="w-full">
                          Add Payment Method
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {bankAccounts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No payment methods added yet. Add your first one!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bankAccounts.map((account) => (
                      <div key={account.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{account.accountName}</h3>
                            <p className="text-sm text-gray-600 mt-1">{account.bankName}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-sm font-mono">{account.accountNumber}</span>
                              <span className="text-sm text-gray-600">Routing: {account.routingNumber}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <CreditCard className="h-12 w-12 text-gray-400" />
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteBankAccount(account.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={showSettingsModal} 
        onClose={() => setShowSettingsModal(false)} 
      />
    </div>
  );
};

export default AdminDashboard;
